import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Cookie, Shield, X } from "lucide-react";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

const CONSENT_KEY = "vb_cookie_consent";

/**
 * CookieConsentBanner
 *
 * Shows a bottom-anchored modal asking the user to accept or decline cookies.
 * Preference is stored in localStorage (for instant hydration) and sent to
 * the backend (which sets an httpOnly cookie_consent cookie for server-side
 * gating of non-essential cookies).
 *
 * If the user has already decided, the banner does not render.
 */
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isAuthenticated } = useSelector((s) => s.auth);

  // Check if user has already decided
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) return; // Already decided — don't show

    // Also check server-side cookie via API
    axios
      .get(`${USER_API}/api/cookies/consent`, { withCredentials: true })
      .then((r) => {
        if (r.data?.decided) {
          localStorage.setItem(CONSENT_KEY, r.data.consent);
        } else {
          // No decision yet — show banner after a short delay
          setTimeout(() => {
            setVisible(true);
            requestAnimationFrame(() => setAnimateIn(true));
          }, 1200);
        }
      })
      .catch(() => {
        // API unreachable — show banner from localStorage state
        setTimeout(() => {
          setVisible(true);
          requestAnimationFrame(() => setAnimateIn(true));
        }, 1200);
      });
  }, []);

  const handleConsent = async (decision) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${USER_API}/api/cookies/consent`,
        { consent: decision },
        { withCredentials: true }
      );
    } catch {
      // If server call fails, still respect the user's choice client-side
    }
    localStorage.setItem(CONSENT_KEY, decision);

    // Animate out
    setAnimateIn(false);
    setTimeout(() => setVisible(false), 350);
    setSubmitting(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          background: "rgba(0,0,0,0.25)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          opacity: animateIn ? 1 : 0,
          transition: "opacity 0.35s ease",
          pointerEvents: animateIn ? "auto" : "none",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          padding: "0 16px 24px",
          transform: animateIn ? "translateY(0)" : "translateY(100%)",
          opacity: animateIn ? 1 : 0,
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1), opacity 0.35s ease",
          pointerEvents: animateIn ? "auto" : "none",
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "#fff",
            borderRadius: 24,
            border: "1px solid hsl(var(--border))",
            padding: "28px 28px 24px",
            boxShadow: "0 -8px 60px rgba(106,0,244,0.10), 0 4px 24px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(106,0,244,0.08)" }}
              >
                <Cookie className="h-5 w-5" style={{ color: "var(--vb-indigo)" }} />
              </div>
              <div>
                <h3 className="font-body text-base font-semibold text-foreground" style={{ margin: 0, lineHeight: 1.3 }}>
                  Cookie Preferences
                </h3>
              </div>
            </div>
          </div>

          {/* Body */}
          <p className="font-body text-sm text-muted-foreground mb-5" style={{ lineHeight: 1.65 }}>
            We use cookies to keep you signed in and improve your experience.
            Essential cookies are always active. You can accept all cookies or
            decline non-essential ones.
          </p>

          {/* What's included */}
          <div
            className="rounded-xl px-4 py-3 mb-5"
            style={{ background: "var(--vb-off)", border: "1px solid rgba(106,0,244,0.06)" }}
          >
            <div className="flex items-start gap-2.5 mb-2">
              <Shield className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--vb-indigo)" }} />
              <div>
                <p className="font-body text-xs font-semibold text-foreground mb-0.5">Essential cookies</p>
                <p className="font-body text-xs text-muted-foreground" style={{ lineHeight: 1.5 }}>
                  Cookie consent preference. Always active — cannot be disabled.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Cookie className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div>
                <p className="font-body text-xs font-semibold text-foreground mb-0.5">Functional cookies</p>
                <p className="font-body text-xs text-muted-foreground" style={{ lineHeight: 1.5 }}>
                  Authentication session, login persistence, device recognition.
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => handleConsent("declined")}
              disabled={submitting}
              className="flex-1 font-body text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              style={{
                height: 46,
                borderRadius: 14,
                border: "1px solid hsl(var(--border))",
                background: "transparent",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.6 : 1,
              }}
            >
              Decline All
            </button>
            <button
              onClick={() => handleConsent("accepted")}
              disabled={submitting}
              className="btn-primary flex-1 justify-center"
              style={{
                height: 46,
                borderRadius: 14,
                fontSize: 14,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.8 : 1,
              }}
            >
              Accept Cookies
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
